import { Controller } from "@hotwired/stimulus";

const AUTOPLAY_SPEED = 5000;

export default class extends Controller {
    connect() {
        this.$track = $(this.element).find(".preview-slider__metal-track");
        this.$dotsContainer = $(this.element).find(".preview-slider__metal-dots");
        this.$tile = $(this.element).closest(".preview-slider__tile--metal");
        this.$link = this.$tile.find(".preview-slider__metal-tile-link");

        this.prepareTrack();

        this.bindTileLinkClick();
        this.initSlider();
    }

    getSlickInstance() {
        if (!this.$track?.hasClass("slick-initialized")) {
            return null;
        }

        try {
            return this.$track.slick("getSlick");
        } catch (error) {
            return null;
        }
    }

    prepareTrack() {
        const slickInstance = this.getSlickInstance();

        if (slickInstance) {
            this.slideCount = slickInstance.slideCount;
            return;
        }

        if (this.$track.hasClass("slick-initialized")) {
            this.restoreTrackSlides();
        }

        this.slideCount = this.$track.children(".preview-slider__metal-slide").length;
    }

    restoreTrackSlides() {
        const slides = [];
        const seen = new Set();

        this.$track.find(".preview-slider__metal-slide").each((_, slide) => {
            const key = slide.querySelector(".preview-slider__metal-text")?.textContent.trim()
                || slide.querySelector(".preview-slider__metal-price")?.textContent.trim()
                || slide.textContent.trim();

            if (!key || seen.has(key)) {
                return;
            }

            seen.add(key);
            slides.push(slide.outerHTML);
        });

        if (slides.length === 0) {
            return;
        }

        try {
            if (this.getSlickInstance()) {
                this.$track.slick("unslick");
            }
        } catch (error) {
            // Ignore broken slick instances and rebuild the track markup below.
        }

        this.$track.removeClass("slick-initialized slick-slider").empty();
        slides.forEach((slideHtml) => {
            this.$track.append(slideHtml);
        });
        this.slideCount = slides.length;
    }

    bindTileLinkClick() {
        if (!this.$link.length) {
            return;
        }

        this.onTrackClick = (event) => {
            if (this.ignoreNextClick) {
                this.ignoreNextClick = false;
                return;
            }

            const slick = this.getSlickInstance();
            if (slick?.dragging || slick?.swiping) {
                return;
            }

            if ($(event.target).closest(".slick-dots, button").length) {
                return;
            }

            const link = this.$link.get(0);
            if (link) {
                link.click();
            }
        };

        this.$track.on("click", this.onTrackClick);
    }

    initSlider() {
        if (this.slideCount <= 1 || this.getSlickInstance() || this.initializing) {
            return;
        }

        this.initializing = true;

        try {
            this.refreshSlider = () => {
                this.syncTrackHeight();

                if (this.getSlickInstance()) {
                    this.$track.slick("setPosition");
                }
            };

            this.syncTrackHeight = () => {
                const trackHeight = this.element.clientHeight;

                if (!trackHeight) {
                    return;
                }

                this.$track.find(".slick-list, .slick-track").height(trackHeight);
                this.$track.find(".slick-slide > div").height(trackHeight);
            };

            this.onSliderChange = (_event, slick, currentSlide) => {
                this.syncDotCount(slick, currentSlide ?? 0);
                this.refreshSlider();
            };

            this.onDotClick = () => {
                this.pauseAutoplay({ resumeAfterInteraction: true });
            };

            this.onSwipe = () => {
                this.ignoreNextClick = true;
                this.pauseAutoplay({ resumeAfterInteraction: true });
            };

            this.$track.on("init reInit afterChange", this.onSliderChange);
            this.$dotsContainer.on("click", "button", this.onDotClick);
            this.$track.on("swipe", this.onSwipe);

            this.$track.slick({
                slidesToShow: 1,
                slidesToScroll: 1,
                infinite: true,
                arrows: false,
                dots: true,
                appendDots: this.$dotsContainer,
                autoplay: true,
                autoplaySpeed: AUTOPLAY_SPEED,
                pauseOnHover: true,
                pauseOnFocus: true,
                pauseOnDotsHover: true,
                swipe: true,
                adaptiveHeight: false,
                customPaging: () => '<button type="button"></button>',
            });

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.syncTrackHeight();
                    this.refreshSlider();
                });
            });

            this.resizeObserver = new ResizeObserver(this.refreshSlider);
            this.resizeObserver.observe(this.element);
            window.addEventListener("resize", this.refreshSlider, { passive: true });
        } finally {
            this.initializing = false;
        }
    }

    pauseAutoplay = ({ resumeAfterInteraction = false } = {}) => {
        if (this.getSlickInstance()) {
            this.$track.slick("slickPause");
        }

        if (resumeAfterInteraction) {
            this.scheduleAutoplayResume();
        }
    };

    scheduleAutoplayResume = () => {
        clearTimeout(this.autoplayResumeTimer);
        this.autoplayResumeTimer = window.setTimeout(() => {
            this.resumeAutoplay();
        }, AUTOPLAY_SPEED);
    };

    resumeAutoplay = () => {
        clearTimeout(this.autoplayResumeTimer);

        if (!this.getSlickInstance()) {
            return;
        }

        const isHovered = this.$track.is(":hover") || this.$dotsContainer.is(":hover");

        if (isHovered || this.element.matches(":focus-within")) {
            this.scheduleAutoplayResume();
            return;
        }

        this.$track.slick("slickPlay");
    };

    syncDotCount(slick, currentSlide) {
        const dotCount = Math.min(slick.slideCount, 4);
        const $dots = slick.$dots?.find("li");

        if (!$dots?.length) {
            return;
        }

        $dots.each(function (index) {
            $(this).toggle(index < dotCount);
        });

        const activeIndex = currentSlide % dotCount;
        $dots.removeClass("slick-active").eq(activeIndex).addClass("slick-active");
    }

    disconnect() {
        clearTimeout(this.autoplayResumeTimer);

        window.removeEventListener("resize", this.refreshSlider);
        this.resizeObserver?.disconnect();

        this.$track?.off("init reInit afterChange", this.onSliderChange);
        this.$track?.off("swipe", this.onSwipe);
        this.$track?.off("click", this.onTrackClick);
        this.$dotsContainer?.off("click", "button", this.onDotClick);

        if (this.getSlickInstance()) {
            this.$track.slick("unslick");
        }
    }
}
